import { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useOrders } from "@/lib/OrdersContext";

const EMPTY = { cliente: "", telefono: "", modelo: "", matricula: "", servicio: "", importe: "", urgente: false };
const FIELDS = [
  { k: "cliente", label: "Cliente", ph: "Nombre del cliente", req: true },
  { k: "telefono", label: "Teléfono", ph: "+34 600 000 000", req: true, type: "tel",
    pattern: "\\+[0-9]{1,3}( ?[0-9]){6,15}", title: "Incluye el prefijo del país, p. ej. +34 600 000 000" },
  { k: "modelo", label: "Modelo", ph: "Seat León 1.5 TSI", req: true },
  { k: "matricula", label: "Matrícula", ph: "1234 ABC", req: true },
];

export const NewOrderDialog = () => {
  const { createOrder } = useOrders();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await createOrder(f);
    setSaving(false);
    if (ok) { setF(EMPTY); setOpen(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-order-btn" className="flex items-center gap-2 rounded-lg bg-[var(--gold)] px-4 py-2.5 text-sm font-bold text-black transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-amber-400">
          <Plus className="h-4 w-4" strokeWidth={3} /> Nueva Orden
        </button>
      </DialogTrigger>
      <DialogContent data-testid="new-order-dialog" className="max-h-[90dvh] overflow-y-auto border-white/10 bg-[#111114] sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">Nueva orden de reparación</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(({ k, label, ph, req, type, pattern, title }) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={k}>{label}</Label>
              <Input id={k} data-testid={`new-order-${k}-input`} required={req} type={type} pattern={pattern} title={title} value={f[k]} onChange={set(k)} placeholder={ph}
                className={k === "matricula" ? "font-mono-plate uppercase" : ""} />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="servicio">Trabajo a realizar</Label>
            <Textarea id="servicio" data-testid="new-order-servicio-input" required value={f.servicio} onChange={set("servicio")} placeholder="Cambio de aceite y filtros, revisión de frenos…" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="importe">Importe (€)</Label>
            <Input id="importe" data-testid="new-order-importe-input" type="number" min="0" step="0.01" required value={f.importe} onChange={set("importe")} placeholder="0,00" />
          </div>
          <label data-testid="new-order-urgente-toggle" className={`flex cursor-pointer items-center gap-2 self-end rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${f.urgente ? "border-orange-500 bg-orange-500/15 text-orange-400" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
            <input type="checkbox" data-testid="new-order-urgente-checkbox" className="accent-orange-500" checked={f.urgente} onChange={(e) => setF({ ...f, urgente: e.target.checked })} />
            Prioritaria / Urgente
          </label>
          <div className="flex items-end justify-end sm:col-span-2">
            <button type="submit" disabled={saving} data-testid="new-order-submit-btn" className="flex h-10 items-center gap-2 rounded-lg bg-[var(--gold)] px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Registrar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
